import {
  fetchFieldCustomizations,
  type FieldCustomizationsResponse,
} from "@/api/user-settings";
import { reloadAppTranslationResources, toSupportedLocale } from "@/i18n";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

type FieldCustomizationOverrideContextValue = {
  isCustomized: (translationKey: string) => boolean;
  refetch: () => void;
};

const FieldCustomizationOverrideContext =
  createContext<FieldCustomizationOverrideContextValue | null>(null);

export function FieldCustomizationOverrideProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { i18n } = useTranslation();
  const lng = toSupportedLocale(i18n.resolvedLanguage ?? i18n.language);

  const { data, refetch } = useQuery<FieldCustomizationsResponse>({
    queryKey: ["settings", "field-customizations", lng],
    queryFn: () => fetchFieldCustomizations(lng),
    // 60 s : au retour sur l'onglet (refetch au focus de react-query), les
    // renommages faits par d'AUTRES utilisateurs de l'instance sont récupérés
    // en ~1 min sans rechargement manuel de la page (cf. effet ci-dessous).
    staleTime: 60 * 1000,
  });

  // Les libellés personnalisés (renommage de champs) sont partagés par toute
  // l'instance mais les traductions i18next sont chargées une fois par session :
  // un collègue gardait donc l'ancien nom jusqu'à un rechargement. Dès que la
  // liste des overrides CHANGE réellement (nouveau fetch au focus/à la sauvegarde),
  // on recharge les ressources i18next de la langue courante → le nouveau libellé
  // apparaît pour tout le monde sans recharger la page.
  const overrideSignature = useMemo(
    () =>
      JSON.stringify(
        (data?.fields ?? [])
          .map((f) => [f.key, f.customLabels] as const)
          .sort((a, b) => a[0].localeCompare(b[0])),
      ),
    [data?.fields],
  );
  const previousSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (data === undefined) return; // pas encore chargé
    if (previousSignatureRef.current === null) {
      // Premier chargement : les traductions sont déjà fraîches, ne pas recharger.
      previousSignatureRef.current = overrideSignature;
      return;
    }
    if (previousSignatureRef.current !== overrideSignature) {
      previousSignatureRef.current = overrideSignature;
      void reloadAppTranslationResources([lng]);
    }
  }, [overrideSignature, data, lng]);

  const customizedKeySet = useMemo(() => {
    const set = new Set<string>();
    for (const field of data?.fields ?? []) {
      set.add(field.key);
    }
    return set;
  }, [data?.fields]);

  const isCustomized = useCallback(
    (translationKey: string) => {
      return customizedKeySet.has(translationKey);
    },
    [customizedKeySet],
  );

  const value = useMemo(
    () => ({
      isCustomized,
      refetch,
    }),
    [isCustomized, refetch],
  );

  return (
    <FieldCustomizationOverrideContext.Provider value={value}>
      {children}
    </FieldCustomizationOverrideContext.Provider>
  );
}

export function useFieldLabelCustomized(translationKey: string): boolean {
  const ctx = useContext(FieldCustomizationOverrideContext);
  if (!ctx) return false;
  return ctx.isCustomized(translationKey);
}

export function useRefetchFieldCustomizations() {
  const ctx = useContext(FieldCustomizationOverrideContext);
  const qc = useQueryClient();
  return useCallback(async () => {
    await qc.invalidateQueries({
      queryKey: ["settings", "field-customizations", "form"],
    });
    await qc.invalidateQueries({ queryKey: ["settings", "field-customizations"] });
    await ctx?.refetch();
  }, [ctx, qc]);
}
